import { Box, CircularProgress, FormControlLabel, Radio, RadioGroup, Typography, styled, useTheme } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ReactComponent as ExcelDownload } from 'src/assets/svg/ExcelDownload.svg'
import { ReactComponent as PdfDownload } from 'src/assets/svg/pdfDownload.svg'
import { useGetGrouppedLocalities } from 'src/api/localities'
import { useGenerateUnifiedExcel, useGenerateUnifiedPdf, useGetUnifiedReports } from 'src/api/report-generator'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MUIThemeColors } from 'src/mui-theme'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { DateRangeSelector } from './DateRangeSelector'
import { ScopeSelector } from './ScopeSelector'
import {
  DateRange,
  buildReportRequest,
  flattenScope,
  formatRangeLabel,
  isDownloadDisabled,
} from './unifiedReportGenerator.action'

interface UnifiedReportGeneratorDialogProps extends ShowDialogProps {
  customerId: number
  customerName: string
}

const UnifiedReportGeneratorDialog: React.FC<UnifiedReportGeneratorDialogProps> = ({
  customerId,
  customerName,
  pop,
}) => {
  const { t } = useTranslation(['reportGenerator', 'report', 'common', 'common-api-message'])
  const today = useMemo(() => new Date(), [])

  const { data: groupedLocalities, isLoading: isScopeLoading } = useGetGrouppedLocalities(customerId)
  const { data: reports, isLoading: isReportsLoading } = useGetUnifiedReports()
  const { mutateAsync: generatePdf, isLoading: isGeneratingPdf } = useGenerateUnifiedPdf()
  const { mutateAsync: generateExcel, isLoading: isGeneratingExcel } = useGenerateUnifiedExcel()

  const scope = useMemo(() => flattenScope(groupedLocalities), [groupedLocalities])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null })

  const selectedReport = useMemo(
    () => reports?.find(report => report.id === selectedReportId),
    [reports, selectedReportId],
  )
  const usesDateRange = selectedReport?.usesDateRange === true

  const handleDownload = (type: 'pdf' | 'excel') => {
    if (!selectedReport) return
    const request = buildReportRequest({
      customerId,
      report: selectedReport,
      selectedLocalityIds: Array.from(selectedIds),
      dateRange,
    })
    const fileName = `${customerName}-${t(`${selectedReport.id}`, { ns: 'report' })}-${new Date().toISOString()}`
    const mutation = type === 'pdf' ? generatePdf : generateExcel
    mutation(request, {
      onSuccess: response => downloadFileLocal(response, fileName),
      onError: () => {
        toast.error(t('InternalError', { ns: 'common-api-message' }))
      },
    })
  }

  const isBaseDisabled = isDownloadDisabled({ selectedCount: selectedIds.size, report: selectedReport, dateRange })

  return (
    <GenericDialogLayout
      title={t('title')}
      pop={pop}
      actions={[
        <GridActionButton
          key="close"
          buttonText={t('close', { ns: 'common' })}
          variant="outlined"
          shadowWeight={0}
          width="100px"
          height="40px"
          onClick={() => pop()}
        />,
      ]}
    >
      <Flex.Row gap={'24px'} alignItems={'flex-start'} sx={{ flexWrap: 'wrap' }}>
        <Column sx={{ alignSelf: 'stretch' }}>
          <StepLabel>{t('step1')}</StepLabel>
          <SectionTitle>{t('scopeTitle')}</SectionTitle>
          {isScopeLoading ? (
            <Loader />
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ScopeSelector scope={scope} selectedIds={selectedIds} onChange={setSelectedIds} />
            </Box>
          )}
        </Column>

        <Column>
          <StepLabel>{t('step2')}</StepLabel>
          <SectionTitle>{t('reportTypeTitle')}</SectionTitle>
          {isReportsLoading ? (
            <Loader />
          ) : (
            <RadioGroup
              value={selectedReportId != null ? String(selectedReportId) : ''}
              onChange={event => setSelectedReportId(Number(event.target.value))}
            >
              {reports?.map(report => (
                <FormControlLabel
                  key={report.id}
                  value={String(report.id)}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">{t(`${report.id}`, { ns: 'report' })}</Typography>}
                />
              ))}
            </RadioGroup>
          )}
          <StepLabel sx={{ mt: '20px' }}>{t('step3')}</StepLabel>
          <SectionTitle>{t('dateRangeTitle')}</SectionTitle>
          <DateRangeSelector range={dateRange} onChange={setDateRange} disabled={!usesDateRange} today={today} />
        </Column>

        <Column>
          <StepLabel>{t('step4')}</StepLabel>
          <SectionTitle>{t('fileTypeTitle')}</SectionTitle>
          <Flex.Column gap={'12px'}>
            <GridActionButton
              buttonIcon={<ExcelDownload height="18px" width="18px" />}
              buttonText={t('downloadExcel')}
              variant="outlined"
              shadowWeight={0}
              width="100%"
              height="48px"
              loading={isGeneratingExcel}
              disabled={isBaseDisabled || !selectedReport?.hasExcelReport || isGeneratingExcel}
              onClick={() => handleDownload('excel')}
            />
            <GridActionButton
              buttonIcon={<PdfDownload height="20px" width="16px" />}
              buttonText={t('downloadPdf')}
              variant="outlined"
              shadowWeight={0}
              width="100%"
              height="48px"
              loading={isGeneratingPdf}
              disabled={isBaseDisabled || !selectedReport?.hasPDFReport || isGeneratingPdf}
              onClick={() => handleDownload('pdf')}
            />
          </Flex.Column>

          <SummaryCard>
            <SummaryTitle>{t('summary')}</SummaryTitle>
            <SummaryRow label={t('scope')} value={t('localitiesSelected', { value: selectedIds.size })} />
            <SummaryRow label={t('date')} value={usesDateRange ? formatRangeLabel(dateRange) : '-'} />
            <SummaryRow label={t('type')} value={selectedReport ? t(`${selectedReport.id}`, { ns: 'report' }) : '-'} />
          </SummaryCard>
        </Column>
      </Flex.Row>
    </GenericDialogLayout>
  )
}

export default UnifiedReportGeneratorDialog

const Loader: React.FC = () => (
  <Flex.Row justifyContent={'center'} sx={{ minHeight: 120, alignItems: 'center' }}>
    <CircularProgress size={28} />
  </Flex.Row>
)

interface SummaryRowProps {
  label: string
  value: string
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value }) => {
  const theme = useTheme()
  return (
    <Flex.Row gap={'6px'}>
      <Typography variant="body2" fontWeight={600} color={getThemeColor(theme, MUIThemeColors.secondaryDark)}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Flex.Row>
  )
}

const Column = styled(Box)`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StepLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const SectionTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 4px;
`

const SummaryCard = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '12px',
  borderRadius: '6px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}))

const SummaryTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 4px;
`
